import { useState } from "react";
import {
  Layer,
  Rectangle,
  ResponsiveContainer,
  Sankey,
  Tooltip,
} from "recharts";

import { hexToRgbA } from "@intelligems/ig-utils";
import { useTheme } from "@mui/material";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { SankeyMetricData } from "@src/types/analytics";

const MyCustomNode = (props: any) => {
  const theme = useTheme();
  const {
    x,
    y,
    width,
    height,
    containerWidth,
    payload,
    index,
    onClick,
    nodeColors,
  } = props;
  const ensuredY = y || 0;
  const barWidth = width * 2;
  const isOut = x + width + 6 > containerWidth;
  const isHidden = payload.name.toLowerCase().includes("hidden");
  const graphValueFormatter = useGraphValueFormatter();
  const percentageOfMax =
    payload.pct !== undefined
      ? (payload.pct * 100).toFixed(1)
      : props.nVisitors != null && props.nVisitors > 0
        ? ((payload.value / props.nVisitors) * 100).toFixed(1)
        : "0.0";
  const getNameLabelHeight = () => {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) {
      return 20;
    }
    ctx.font = "600 12px sans-serif";
    const maxWidth = 150;
    const lineHeight = 12 * 1.2;
    const words = payload.name.split(" ");
    let lines = 1;
    let currentLine = "";
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines++;
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    return lines * lineHeight;
  };

  const textHeight = getNameLabelHeight();
  const verticalSpacing = 16;
  const totalTextHeight = textHeight + verticalSpacing * 2;
  const [isHovered, setIsHovered] = useState(false);

  const nodeColor =
    nodeColors?.[payload.name] ?? theme.igBackgroundColors.accentSolid;

  const TRANSITION_TIME_ON_HOVER = "200ms";
  const OPACITY_ON_HOVER = "0.5";
  const STYLE_FILL_OPACITY_TRANSITION = {
    style: {
      transition: `fill-opacity ${TRANSITION_TIME_ON_HOVER} ease`,
    },
  };
  const OPACITY_TRANSITION = {
    transition: `opacity ${TRANSITION_TIME_ON_HOVER} ease`,
  };

  return (
    <Layer
      key={`CustomNode${index}`}
      {...(onClick && {
        onClick: () => onClick?.(payload.name),
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        style: {
          cursor: "pointer",
        },
      })}
    >
      {/* Transparent hit area covering the full node extent (bar + label region) */}
      {onClick && (
        <rect
          x={(isOut ? x - 60 : x) - 6}
          y={ensuredY + Math.min(0, (height - totalTextHeight) / 2) - 6}
          width={(isOut ? barWidth + 60 : barWidth + 164) + 12}
          height={Math.max(height, totalTextHeight) + 12}
          rx={6}
          fill="black"
          fillOpacity={0}
        />
      )}

      <Rectangle
        x={x}
        y={ensuredY}
        width={isHidden ? barWidth * 0.5 : barWidth}
        height={height}
        fill={nodeColor}
        fillOpacity={isHidden ? "0.15" : isHovered ? OPACITY_ON_HOVER : "1"}
        {...STYLE_FILL_OPACITY_TRANSITION}
      />
      {!isHidden && (
        <>
          <foreignObject
            x={isOut ? x - 46 : x + barWidth + 14}
            y={ensuredY + (height - totalTextHeight) / 2}
            width={150}
            height={textHeight}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                textAlign: isOut ? "right" : "left",
                wordWrap: "break-word",
                width: "100%",
                lineHeight: "1.2",
                opacity: isHovered ? OPACITY_ON_HOVER : "1",
                ...OPACITY_TRANSITION,
              }}
            >
              {payload.name}
            </div>
          </foreignObject>
          <text
            textAnchor={isOut ? "end" : "start"}
            x={isOut ? x - 6 : x + barWidth + 14}
            y={
              ensuredY +
              (height - totalTextHeight) / 2 +
              textHeight +
              verticalSpacing
            }
            fontSize="12"
            strokeOpacity="0.5"
            fontWeight="400"
            fillOpacity={isHidden ? "0.15" : isHovered ? OPACITY_ON_HOVER : "1"}
            {...STYLE_FILL_OPACITY_TRANSITION}
          >
            {`${percentageOfMax}%`}
          </text>
          <text
            textAnchor={isOut ? "end" : "start"}
            x={isOut ? x - 6 : x + barWidth + 14}
            y={
              ensuredY +
              (height - totalTextHeight) / 2 +
              textHeight +
              verticalSpacing * 2
            }
            fontSize="12"
            strokeOpacity="0.5"
            fontWeight="400"
            fillOpacity={isHidden ? "0.15" : isHovered ? OPACITY_ON_HOVER : "1"}
            {...STYLE_FILL_OPACITY_TRANSITION}
          >
            {`${graphValueFormatter({ isAxisTick: false, value: payload.value, configDataTypes: ["numberNoDecimal"] })} Visitors`}
          </text>
        </>
      )}
    </Layer>
  );
};

const MyCustomLink = (props: any) => {
  const {
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourceControlX,
    targetControlX,
    linkWidth,
    payload,
    linkColors,
  } = props;
  const theme = useTheme();
  const defaultColor = hexToRgbA(theme.igBackgroundColors.accentSolid, 0.75);
  const color =
    (payload?.target?.name && linkColors?.[payload.target.name]) ||
    defaultColor;
  const d = `M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`;

  return (
    <path
      d={d}
      fill="none"
      stroke={color}
      strokeWidth={linkWidth}
      strokeOpacity={0.2}
    />
  );
};

interface SankeyGraphProps {
  nodes: { name: string; pct?: number }[];
  links: SankeyMetricData["links"];
  nVisitors?: number;
  height?: number;
  onNodeClick?: (nodeName: string) => void;
  /** Map of node name → color override */
  nodeColors?: Record<string, string>;
  /** Map of link target node name → color override */
  linkColors?: Record<string, string>;
}

export const SankeyGraph = ({
  nodes,
  links,
  nVisitors,
  height = 350,
  onNodeClick,
  nodeColors,
  linkColors,
}: SankeyGraphProps) => {
  const processedLinks = links.map((link) => {
    const originalValue = link.value;
    // Use a minimum threshold for rendering but keep original value for display
    return {
      ...link,
      originalValue, // Store the original value
      value: Math.max(originalValue, 0.001), // Minimum value for rendering
    };
  });

  if (nodes.length === 0 || processedLinks.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width={"100%"} height={height}>
      <Sankey
        data={{ nodes, links: processedLinks }}
        node={
          <MyCustomNode
            onClick={onNodeClick}
            nVisitors={nVisitors}
            nodeColors={nodeColors}
          />
        }
        sort={false}
        nodePadding={60}
        margin={{
          left: 20,
          right: 250,
          top: 50,
          bottom: 50,
        }}
        link={<MyCustomLink linkColors={linkColors} />}
      >
        <Tooltip />
      </Sankey>
    </ResponsiveContainer>
  );
};
